import { useTranslation } from "react-i18next";
import BaseButton from "@/components/base/button/BaseButton.tsx";
import CommonInfoLink from "@/components/common/CommonInfoLink.tsx";
import IconAlertCircleLinear from "@/assets/icons/alert-circle-linear.svg?react";
import { AuthPayload, AuthSchema } from "./auth.schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useUsernameCheck } from "@/shared/hooks/useUsernameCheck";
import {
  useGetProfile,
  useSharePhone,
  formatPhoneMasked,
} from "../profile/api";
import { parseStartAppString } from "@/shared/utils";
import { cn } from "@/lib/utils";
import IconChevron from "@/assets/icons/chevron.svg?react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useProfileUpdate } from "../profile/edit/edit-profile.api";
import { useNavigate } from "react-router";
import PrivacyPolicy from "@/components/common/PrivacyPolicy";
import AgeConfirmationDialog from "./AgeConfirmationDialog";
import { useCallback, useEffect, useState } from "react";
import Spinner from "@/components/common/Spinner";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer.tsx";
import IconClose from "@/assets/icons/close.svg?react";
import { initData, useSignal } from "@telegram-apps/sdk-react";
import { useQueryClient } from "@tanstack/react-query";
import { TranslationKey } from "@/shared/hooks/useTranslationLoader";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import { useHCaptcha } from "@/shared/hooks/useHCaptcha";

const AuthPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  useUsernameCheck();
  const [isDebouncing] = useState(false);
  const [isCheckingUsername] = useState(false);
  const { refetch, data: profile } = useGetProfile();

  // Referral gate: users who arrived via a referral link (start param carries
  // `ref_<code>`) MUST share a phone number before finishing account creation —
  // the inviter's DON reward is only granted once this phone is set server-side.
  // Non-referral users are unaffected. Referral signal is read from the same
  // start param App.tsx uses (via parseStartAppString); we don't touch App.tsx.
  const startParam = useSignal(initData.startParam);
  const isReferral = !!parseStartAppString(startParam)?.ref;
  const hasPhone = !!profile?.phone;
  const { sharePhone, isPending: isSettingPhone } = useSharePhone({
    onSuccess: () => {
      refetch();
    },
  });
  const [, setIsPrivacyPolicyOpen] = useState(false);
  const [language, setLanguage] = useState(i18n.language);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isOpenAgeDialog, setIsOpenAgeDialog] = useState(false);
  const [isUsernameAvailable] = useState(true);
  const [hasSpecialCharacters] = useState(false);
  const queryClient = useQueryClient();
  const hcaptcha = useHCaptcha();

  const data = useSignal(initData.user);

  const handlePrivacyPolicyOpen = useCallback(() => {
    setIsPrivacyPolicyOpen(true);
  }, []);

  const form = useForm<AuthPayload>({
    mode: "onChange",
    resolver: zodResolver(AuthSchema),
    defaultValues: {
      full_name: [data?.last_name, data?.first_name]
        .filter(Boolean)
        .join(" "),
      is_user_confirmed_age: false,
    },
  });

  const { mutateAsync: updateProfile, isPending: updatingProfile } =
    useProfileUpdate(true);

  const onSubmit = async (data: AuthPayload) => {
    if (!isUsernameAvailable || hasSpecialCharacters) {
      toast.error(
        t(
          hasSpecialCharacters
            ? "username_validation_error"
            : "username_not_available"
        )
      );
      return;
    }
    const isUserConfirmedAge = data.is_user_confirmed_age;

    if (!isUserConfirmedAge) {
      toast.error(t("age_confirmation_required_error"));
      return;
    }

    // Referral users must share a phone before account creation can complete.
    if (isReferral && !hasPhone) {
      toast.error(t("referral_phone_required"));
      return;
    }

    // hCaptcha — background bot detection
    const isHuman = await hcaptcha.verify();
    if (!isHuman) {
      toast.error(t("global_error"));
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { is_user_confirmed_age, ...otherData } = data;

    await updateProfile({
      ...otherData,
    });
    await refetch();
    navigate("/");
  };

  const languageList = [
    {
      title: "O'zbekcha",
      icon: "images/flags/uz.svg",
      key: "uz",
    },
    {
      title: "English",
      icon: "images/flags/en.svg",
      key: "en",
    },
    {
      title: "Русский",
      icon: "images/flags/ru.svg",
      key: "ru",
    },
  ];

  async function changeLocale(lang: string) {
    await i18n.changeLanguage(lang);
    queryClient.invalidateQueries({
      queryKey: [TranslationKey.Translations, lang],
    });
    setOpenDrawer(false);
    setIsOpenAgeDialog(true);
  }

  useEffect(() => {
    const isFirstVisit = localStorage.getItem("hasVisitedAuth") === null;
    if (isFirstVisit) {
      setOpenDrawer(true);
      localStorage.setItem("hasVisitedAuth", "true");
    }
  }, []);

  useEffect(() => {
    setOpenDrawer(true);
  }, []);

  return (
    <div
      className="flex flex-col justify-center gap-10 py-6 container"
      style={{
        height:
          "calc(var(--tg-viewport-height, 100vh) - var(--safe-area-inset-top))",
      }}
    >
      <Drawer
        open={openDrawer}
        onOpenChange={(e) => {
          setOpenDrawer(e);
          setIsOpenAgeDialog(!e);
        }}
      >
        <DrawerContent className="bg-gray-4" showTopThumb>
          <DrawerHeader className="flex justify-between items-center">
            <DrawerTitle className="text-lg" data-testid="title">
              {t("change_language")}
            </DrawerTitle>
            <DrawerClose>
              <IconClose className="size-6 text-gray-1 cursor-pointer" />
            </DrawerClose>
          </DrawerHeader>
          <DrawerBody>
            <div className="flex flex-col justify-center -mt-4 -mr-4">
              {languageList.map((item, index) => (
                <VibratingButton
                  vibrationType="selection"
                  className="border-b border-white/08 overflow-hidden pr-4 flex-center-between last-of-type:border-b-0"
                  key={index}
                  onClick={() => setLanguage(item.key)}
                >
                  <div
                    className="flex-y-center gap-3 py-2.5 relative group"
                    key={index}
                  >
                    <div className="w-8 h-8 bg-white/08 rounded-lg flex-center shrink-0">
                      <img
                        className="object-cover w-6 h-6"
                        src={item.icon}
                        alt=""
                      />
                    </div>
                    <p className="w-full text-sm font-semibold leading-130 text-white">
                      {item.title}
                    </p>
                  </div>
                  <div>
                    <input
                      checked={[language].includes(item.key)}
                      className="w-0 h-0 peer hidden"
                      type="radio"
                      readOnly
                    />
                    <div className="w-6 h-6 rounded-full border border-white/08 peer-checked:bg-blue peer-checked:border-blue transition-200 relative after:absolute after:left-1/2 after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:w-3 after:h-3 after:rounded-full after:bg-white after:duration-200 after:opacity-0 peer-checked:after:opacity-100 peer-checked:after:drop-shadow-[0_0_10px_#00224B]" />
                  </div>
                </VibratingButton>
              ))}
              <div className="pr-4">
                <BaseButton
                  className="w-full mt-4"
                  text={t("save")}
                  onClick={() => changeLocale(language)}
                  vibrate={true}
                  vibrationType="soft"
                ></BaseButton>
              </div>
            </div>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      {/*<PrivacyPolicyDrawer*/}
      {/*  onClose={handlePrivacyPolicyClose}*/}
      {/*  isOpen={isPrivacyPolicyOpen}*/}
      {/*/>isPrivacyPolicyOpen*/}
      <img className="h-8" src="images/logo.svg" alt="" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-3 flex-1 justify-between"
        >
          <div className="space-y-3">
            <FormField
              control={form.control}
              name="full_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("full_name")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("full_name_placeholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/*<FormField*/}
            {/*  control={form.control}*/}
            {/*  name="username"*/}
            {/*  render={({ field }) => (*/}
            {/*    <FormItem>*/}
            {/*      <FormLabel>{t("username")}</FormLabel>*/}
            {/*      <FormControl>*/}
            {/*        <UsernameForm*/}
            {/*          {...field}*/}
            {/*          onAvailabilityChange={setIsUsernameAvailable}*/}
            {/*          onValidationChange={(isValid) =>*/}
            {/*            setHasSpecialCharacters(!isValid)*/}
            {/*          }*/}
            {/*          onDebouncingChange={setIsDebouncing} // Pass handler*/}
            {/*          onCheckLoading={setIsCheckingUsername} // Pass handler*/}
            {/*          initialUsername={profileData?.username ?? ""}*/}
            {/*        />*/}
            {/*      </FormControl>*/}
            {/*      /!* <FormMessage /> *!/*/}
            {/*    </FormItem>*/}
            {/*  )}*/}
            {/*/>*/}
            {hasSpecialCharacters && (
              <CommonInfoLink
                noLink
                text={t("username_validation_error")}
                icon={
                  <IconAlertCircleLinear
                    className="shrink-0"
                    width="20"
                    height="20"
                  />
                }
              />
            )}
            {isReferral && (
              <FormItem>
                <FormLabel>{t("phone_number") || "Telefon raqam"}</FormLabel>
                {hasPhone ? (
                  <div className="bg-gray-4 text-white p-2.5 rounded-10 border-b border-white/08 flex justify-between items-center gap-2">
                    <span className="overflow-hidden line-clamp-1 pr-2 w-full">
                      {formatPhoneMasked(profile?.phone)}
                    </span>
                    <div className="size-5 rounded-full bg-green/15 flex-center shrink-0">
                      <Check className="size-3 text-green" strokeWidth={3} />
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={isSettingPhone ? undefined : sharePhone}
                    className={cn(
                      "bg-gray-4 text-white p-2.5 rounded-10 border border-orange-100/40 flex justify-between items-center transition-colors active:bg-[#101017]",
                      isSettingPhone
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer"
                    )}
                  >
                    {isSettingPhone ? (
                      <Loader2 className="mx-auto size-5 animate-spin text-orange-100" />
                    ) : (
                      <>
                        <span className="overflow-hidden line-clamp-1 pr-2 w-full">
                          {t("share_phone_via_telegram") ||
                            "Telegram orqali ulashish"}
                        </span>
                        <IconChevron
                          className="h-5 w-5 text-orange-100 flex-shrink-0"
                          strokeWidth="1"
                        />
                      </>
                    )}
                  </div>
                )}
                {!hasPhone && (
                  <p className="text-xs text-gray-1 mt-1">
                    {t("referral_phone_required")}
                  </p>
                )}
              </FormItem>
            )}
          </div>
          <div className="space-y-3">
            <PrivacyPolicy onClick={handlePrivacyPolicyOpen} />
            <BaseButton
              type="submit"
              className="transition-all w-full"
              text={t("continue")}
              disabled={updatingProfile || isDebouncing || isCheckingUsername}
              prefix={updatingProfile && <Spinner />}
              vibrate={true}
              vibrationType="soft"
            />
          </div>
          <div ref={hcaptcha.containerRef} style={{ display: "none" }} />
        </form>
      </Form>
      <AgeConfirmationDialog
        isOpen={isOpenAgeDialog}
        onOpenChange={setIsOpenAgeDialog}
        onConfirm={() => {
          form.setValue("is_user_confirmed_age", true);
        }}
      />
    </div>
  );
};

export default AuthPage;
